import React, { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LifeBuoy, Send, Mail, Check } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "support@calenlingo.com";

export default function SupportDialog({ onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support/message", { subject, message });
      setSent(true);
      toast.success("Message received — we usually reply within a day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send. Try emailing us directly.");
    } finally {
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      toast.success("Support email copied");
    } catch { /* ignore */ }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md bg-card border rounded-2xl p-0 overflow-hidden animate-slide-up"
        data-testid="support-dialog"
      >
        <div className="bg-secondary text-secondary-foreground px-6 py-5">
          <DialogHeader>
            <div className="text-xs uppercase tracking-widest text-secondary-foreground/70 flex items-center gap-2">
              <LifeBuoy className="w-3.5 h-3.5 text-honey" strokeWidth={2} /> We're here
            </div>
            <DialogTitle className="font-display text-2xl mt-1 text-secondary-foreground">
              How can we help?
            </DialogTitle>
            <DialogDescription className="text-secondary-foreground/70 sr-only">
              Contact CalenLingo support
            </DialogDescription>
          </DialogHeader>
        </div>

        {sent ? (
          <div className="px-6 py-8 text-center space-y-4" data-testid="support-sent">
            <div className="w-14 h-14 rounded-full bg-forest/10 text-forest mx-auto flex items-center justify-center">
              <Check className="w-7 h-7" strokeWidth={2} />
            </div>
            <h3 className="font-display text-2xl tracking-tight">Message sent</h3>
            <p className="text-sm text-muted-foreground">
              A real human will read this and reply to your account email. Usually within a day.
            </p>
            <Button
              onClick={onClose}
              className="rounded-full h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white"
              data-testid="support-close-button"
            >
              Back to my calendar
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="support-subject">Subject</Label>
              <Input
                id="support-subject"
                data-testid="support-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required maxLength={140}
                placeholder="e.g. Can I switch from Pro to Lifetime?"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-message">Message</Label>
              <textarea
                id="support-message"
                data-testid="support-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required minLength={1} maxLength={4000}
                rows={5}
                placeholder="Tell us what's happening — bug, question, or a great idea."
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-y"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={copyEmail}
                data-testid="support-copy-email"
                className="text-xs text-muted-foreground hover:text-terracotta flex items-center gap-1.5"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.75} /> or email {SUPPORT_EMAIL}
              </button>
              <Button
                type="submit" disabled={busy}
                data-testid="support-submit-button"
                className="rounded-full h-10 px-4 bg-terracotta hover:bg-terracotta/90 text-white"
              >
                <Send className="w-4 h-4 mr-2" strokeWidth={1.75} />
                {busy ? "Sending…" : "Send"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
